import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Headset, Bell } from "lucide-react";
import { adminApi } from "@/lib/api";
import { playChime } from "@/lib/chime";

// Runs on EVERY admin page (mounted in AdminLayout). Polls the live-chat queue
// and alerts on a NEW guest request:
//   • another tab / window / minimized (document.hidden) → a WhatsApp-style
//     desktop/OS notification (needs one-time "Allow") + sound
//   • admin app focused, not on /live-chats → an on-screen toast + sound
//   • on /live-chats & focused → left to the console itself (no double alert)
export default function LiveChatNotifier() {
    const navigate = useNavigate();
    const location = useLocation();
    const seenRef = useRef(null); // Set of queue session_ids from the last tick (null = first load)
    const locRef = useRef(location.pathname);
    locRef.current = location.pathname;

    // One-time nudge to enable desktop notifications (the toast's action button
    // provides the user gesture browsers require for the permission prompt).
    useEffect(() => {
        if (typeof window === "undefined" || !("Notification" in window)) return;
        if (Notification.permission !== "default") return;
        toast("Turn on desktop alerts", {
            description: "Get notified of new live chats even when you're on another tab or window.",
            icon: <Bell size={16} />,
            duration: 15000,
            action: {
                label: "Enable",
                onClick: () => {
                    try {
                        Notification.requestPermission();
                    } catch (e) {
                        /* ignore */
                    }
                },
            },
        });
    }, []);

    useEffect(() => {
        let stopped = false;

        const who = (c) => c.visitor?.name || `Guest ${String(c.session_id).slice(0, 6)}`;

        const showToast = (c) => {
            toast(`New live chat from ${who(c)}`, {
                description: c.preview || "A guest is waiting for a live agent.",
                icon: <Headset size={16} />,
                duration: 10000,
                action: { label: "Open", onClick: () => navigate("/live-chats") },
            });
        };

        const osNotify = (c) => {
            if (!("Notification" in window) || Notification.permission !== "granted") {
                showToast(c); // fallback so the alert isn't lost
                return;
            }
            try {
                const n = new Notification("New live chat — Orchid", {
                    body: `${who(c)} is waiting for a live agent.`,
                    tag: `orchid-livechat-${c.session_id}`,
                    icon: `${window.location.origin}/favicon.ico`,
                });
                n.onclick = () => {
                    window.focus();
                    navigate("/live-chats");
                    n.close();
                };
            } catch (e) {
                showToast(c);
            }
        };

        const tick = async () => {
            try {
                const { data } = await adminApi.get("/api/live/chats");
                const queue = data.queue || [];
                const ids = new Set(queue.map((c) => c.session_id));
                const prev = seenRef.current;
                // Skip the first load so already-waiting chats don't all fire at once.
                if (prev !== null) {
                    const fresh = queue.filter((c) => !prev.has(c.session_id));
                    if (fresh.length) {
                        const hidden = typeof document !== "undefined" && document.hidden;
                        const onLiveChats = locRef.current === "/live-chats";
                        if (hidden) {
                            playChime();
                            fresh.forEach(osNotify);
                        } else if (!onLiveChats) {
                            playChime();
                            fresh.forEach(showToast);
                        }
                    }
                }
                seenRef.current = ids;
            } catch (e) {
                /* transient — retry next tick */
            }
        };

        tick();
        const id = setInterval(() => {
            if (!stopped) tick();
        }, 4000);
        return () => {
            stopped = true;
            clearInterval(id);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
}
